// web/src/components/admin/PipelineControl.tsx

import { useState, useEffect, useCallback } from 'react';
import { Play, Loader, CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp } from 'react-feather';
import { PipelineJob, PipelineStatusResponse, PipelineJobStatus, PipelineStep, PipelineMode } from '../../types/pipeline';

interface ContentSource {
  id: string;
  name: string;
  isActive: boolean;
}

interface PipelineControlProps {
  onJobStarted?: (job: PipelineJob) => void;
  onStatusChange?: (status: PipelineStatusResponse['data']) => void;
}

const ALL_STEPS: PipelineStep[] = ['crawl', 'chunk', 'embed', 'index'];

const MODE_LABELS: Record<PipelineMode, { label: string; description: string }> = {
  'clear-and-update': {
    label: 'Clear and Update',
    description: 'Clear existing index and rebuild with fresh content (recommended)',
  },
  'clear-only': {
    label: 'Clear Only',
    description: 'Remove content from index without adding new content',
  },
  'update-only': {
    label: 'Update Only',
    description: 'Add/update content without clearing existing index',
  },
};

export default function PipelineControl({ onJobStarted, onStatusChange }: PipelineControlProps) {
  const [status, setStatus] = useState<PipelineStatusResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Options panel state
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentSources, setContentSources] = useState<ContentSource[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedSteps, setSelectedSteps] = useState<PipelineStep[]>([]);
  const [mode, setMode] = useState<PipelineMode>('clear-and-update');
  const [loadingSources, setLoadingSources] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/pipeline/status');
      const data: PipelineStatusResponse = await response.json();

      if (data.success) {
        setStatus(data.data);
        onStatusChange?.(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch pipeline status:', err);
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  const fetchContentSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      const response = await fetch('/api/admin/content-sources');
      const data = await response.json();
      if (data.success) {
        const activeSources = data.data.filter((s: ContentSource) => s.isActive);
        setContentSources(activeSources);
      }
    } catch (err) {
      console.error('Failed to fetch content sources:', err);
    } finally {
      setLoadingSources(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchStatus();
    fetchContentSources();

    // Poll every 5 seconds when running
    const interval = setInterval(() => {
      if (status?.hasRunningJob) {
        fetchStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchStatus, fetchContentSources, status?.hasRunningJob]);

  const startPipeline = async () => {
    setStarting(true);
    setError(null);

    try {
      const body: {
        mode: PipelineMode;
        sources?: string[] | 'all';
        steps?: PipelineStep[] | 'all';
      } = {
        mode,
        sources: selectedSources.length === 0 ? 'all' : selectedSources,
        steps: selectedSteps.length === 0 ? 'all' : selectedSteps,
      };

      const response = await fetch('/api/admin/pipeline/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        onJobStarted?.(data.data);
        fetchStatus();
        // Collapse options after starting
        setIsExpanded(false);
      } else {
        setError(data.error || 'Failed to start pipeline');
      }
    } catch (err) {
      setError('Failed to start pipeline');
    } finally {
      setStarting(false);
    }
  };

  const handleSourceToggle = (sourceId: string) => {
    setSelectedSources((prev) =>
      prev.includes(sourceId) ? prev.filter((id) => id !== sourceId) : [...prev, sourceId]
    );
  };

  const handleAllSourcesToggle = () => {
    if (selectedSources.length === 0) {
      // Currently "all" - switch to none selected (which means select all explicitly)
      // Actually, empty array means "all", so toggle to explicit selection of all
      setSelectedSources(contentSources.map((s) => s.id));
    } else {
      // Some or all selected - clear to use "all"
      setSelectedSources([]);
    }
  };

  const handleStepToggle = (step: PipelineStep) => {
    setSelectedSteps((prev) =>
      prev.includes(step) ? prev.filter((s) => s !== step) : [...prev, step]
    );
  };

  const handleAllStepsToggle = () => {
    if (selectedSteps.length === 0) {
      setSelectedSteps([...ALL_STEPS]);
    } else {
      setSelectedSteps([]);
    }
  };

  const getStatusIcon = (jobStatus: PipelineJobStatus) => {
    switch (jobStatus) {
      case 'running':
      case 'pending':
        return <Loader className="animate-spin text-blue-500" size={20} />;
      case 'completed':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'failed':
        return <XCircle className="text-red-500" size={20} />;
      case 'cancelled':
        return <AlertTriangle className="text-yellow-500" size={20} />;
      default:
        return null;
    }
  };

  const getStatusText = (jobStatus: PipelineJobStatus) => {
    switch (jobStatus) {
      case 'running':
        return 'Running';
      case 'pending':
        return 'Starting...';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const getButtonText = () => {
    if (starting) return 'Starting...';
    if (status?.hasRunningJob) return 'Pipeline Running';

    const sourcesText =
      selectedSources.length === 0
        ? 'All Sources'
        : selectedSources.length === 1
          ? '1 Source'
          : `${selectedSources.length} Sources`;

    const stepsText =
      selectedSteps.length === 0
        ? 'Full Pipeline'
        : selectedSteps.length === 1
          ? selectedSteps[0].charAt(0).toUpperCase() + selectedSteps[0].slice(1)
          : `${selectedSteps.length} Steps`;

    if (mode === 'clear-only') {
      return `Clear ${sourcesText}`;
    }

    return `Run ${stepsText} - ${sourcesText}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <Loader className="animate-spin text-gray-400" size={24} />
          <span className="ml-2 text-gray-500">Loading pipeline status...</span>
        </div>
      </div>
    );
  }

  const runningJob = status?.runningJob;
  const environment = status?.environment || 'dev';
  const allSourcesSelected = selectedSources.length === 0;
  const allStepsSelected = selectedSteps.length === 0;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <span>Pipeline Control</span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  environment === 'production'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {environment.toUpperCase()}
              </span>
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Run the content pipeline (crawl, chunk, embed, index) for your sources
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <span>Options</span>
            </button>

            <button
              onClick={startPipeline}
              disabled={starting || status?.hasRunningJob}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                starting || status?.hasRunningJob
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {starting ? (
                <Loader className="animate-spin" size={18} />
              ) : status?.hasRunningJob ? (
                <Loader className="animate-spin" size={18} />
              ) : (
                <Play size={18} />
              )}
              <span>{getButtonText()}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Expandable Options Panel */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Content Sources */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Content Sources</h4>
              {loadingSources ? (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader className="animate-spin mr-2" size={14} />
                  Loading sources...
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allSourcesSelected}
                      onChange={handleAllSourcesToggle}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      All Sources ({contentSources.length})
                    </span>
                  </label>
                  <div className="ml-6 space-y-1">
                    {contentSources.map((source) => (
                      <label
                        key={source.id}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!allSourcesSelected && selectedSources.includes(source.id)}
                          disabled={allSourcesSelected}
                          onChange={() => handleSourceToggle(source.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <span
                          className={`text-sm ${allSourcesSelected ? 'text-gray-400' : 'text-gray-700'}`}
                        >
                          {source.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pipeline Steps */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Pipeline Steps</h4>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allStepsSelected}
                    onChange={handleAllStepsToggle}
                    disabled={mode === 'clear-only'}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span
                    className={`text-sm font-medium ${mode === 'clear-only' ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    All Steps (4)
                  </span>
                </label>
                <div className="ml-6 space-y-1">
                  {ALL_STEPS.map((step) => (
                    <label key={step} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!allStepsSelected && selectedSteps.includes(step)}
                        disabled={allStepsSelected || mode === 'clear-only'}
                        onChange={() => handleStepToggle(step)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span
                        className={`text-sm capitalize ${allStepsSelected || mode === 'clear-only' ? 'text-gray-400' : 'text-gray-700'}`}
                      >
                        {step}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Mode */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Mode</h4>
              <div className="space-y-2">
                {(Object.keys(MODE_LABELS) as PipelineMode[]).map((modeKey) => (
                  <label key={modeKey} className="flex items-start space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pipeline-mode"
                      checked={mode === modeKey}
                      onChange={() => setMode(modeKey)}
                      className="mt-0.5 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {MODE_LABELS[modeKey].label}
                        {modeKey === 'clear-and-update' && (
                          <span className="ml-1 text-xs text-green-600">(recommended)</span>
                        )}
                      </span>
                      <p className="text-xs text-gray-500">{MODE_LABELS[modeKey].description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Running Job Status */}
      {runningJob && (
        <div className="border-t border-gray-200 p-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getStatusIcon(runningJob.status)}
                <div>
                  <p className="font-medium text-gray-900">{getStatusText(runningJob.status)}</p>
                  {runningJob.progress && (
                    <p className="text-sm text-gray-600">
                      {runningJob.progress.currentStep && (
                        <span className="capitalize">{runningJob.progress.currentStep}</span>
                      )}
                      {runningJob.progress.currentSource && (
                        <span> - {runningJob.progress.currentSource}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {runningJob.progress && (
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {runningJob.progress.completedSources.length} /{' '}
                    {runningJob.progress.totalSources} sources
                  </p>
                  <div className="mt-1 w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 rounded-full h-2 transition-all"
                      style={{
                        width: `${
                          (runningJob.progress.completedSources.length /
                            runningJob.progress.totalSources) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {runningJob.startedAt && (
              <div className="mt-2 flex items-center text-xs text-gray-500">
                <Clock size={12} className="mr-1" />
                Started: {new Date(runningJob.startedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
